/**
 * Tenant Header Injection
 * 
 * Utilities for automatically injecting tenant context into API requests.
 * This allows domain services to not worry about passing clubId/tenantHandle manually.
 * 
 * Uses appRuntime service which is initialized before React renders.
 * 
 * @module getTenantHeaders
 */

import { appRuntime } from '~/services/appRuntime';
import { IS_PRODUCTION } from '../constants';

/**
 * Tenant context for API headers
 */
export interface TenantHeaders {
  tenantHandle: string | null;
  clubId: string | null;
}

/**
 * Get tenant headers for API request
 * 
 * Automatically injects tenant context from appRuntime if not provided in options.
 * Options take precedence over appRuntime (for testing or special cases).
 * 
 * @param options - Request options that may contain tenant context
 * @returns Tenant context to use for headers
 * 
 * @example
 * ```typescript
 * // In apiClient.ts
 * const tenantHeaders = getTenantHeaders(options);
 * const headers = buildHeaders({
 *   ...options,
 *   tenantHandle: tenantHeaders?.tenantHandle,
 *   clubId: tenantHeaders?.clubId,
 * });
 * ```
 */
export function getTenantHeaders(options?: {
  tenantHandle?: string;
  clubId?: number | string;
}): TenantHeaders | null {
  // If tenant context is explicitly provided in options, use it
  if (options?.tenantHandle && options?.clubId !== undefined) {
    return {
      tenantHandle: options.tenantHandle,
      clubId: String(options.clubId),
    };
  }
  
  // Otherwise, use global tenant context from appRuntime
  const clubId = appRuntime.clubId;
  const tenantHandle = appRuntime.tenantHandle;
  
  // Only return if we have at least one value
  if (!clubId && !tenantHandle) {
    if (!IS_PRODUCTION && appRuntime.isInitialized) {
      console.warn(
        '[ApiClient] ⚠️ No tenant context available in URL',
        '\nAPI request will be made without tenant headers.',
        '\nThis may cause API errors if the endpoint requires tenant context.',
        '\nEnsure URL contains ?club-id=XXX&tenant-handle=YYY parameters.'
      );
    }
    return null;
  }
  
  return {
    clubId,
    tenantHandle,
  };
}
